import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';

import { AcceptPaymentForm, AuthorizeProfilePaymentComponent, GetPaymentMethods, UpdatePaymentMethod, DeletePaymentMethod } from './AcceptJs';
import { notifyError, logError } from '../UserMessaging';
import { mapStateToPlaceOrderDetails } from '../PlaceOrder';
import { OptionListItemRadio } from '../../components';
import { selectPaymentOption, updateAcceptJsClientToken, updateAcceptJsClientPaymentMethods, clearAcceptJsClientPaymentProfile, selectAcceptJsClientPaymentProfile, updateAcceptJsPaymentCard, updateAcceptJsPaymentData } from './paymentMethodActionCreators';

const AuthorizeNetScriptUrl = {
  Production: 'https://js.authorize.net/v1/Accept.js',
  Sandbox: 'https://jstest.authorize.net/v1/Accept.js'
};

const AcceptJsPaymentComponent = (props) => {
  const {
    isSelected,
    AcceptJsClientToken,
    settings,
    amount,
    values,
    AcceptJsClientPaymentMethods,
    AuthorizeDotNetPaymentProfileId,
    countriesStatesMap,
    acceptJsPaymentId,
    onPaymentIsValid,
    onPaymentCancel,
    onAcceptCardDataUpdate,
    onProfilePaymentSelected,
    logDevError,
    onPaymentMethodsReceived,
    onAcceptDataLoadFailed,
    onSuccesfulRequest,
    onProfilePaymentCleared
  } = props;

  const [state, setState] = useState({
    acceptInitializing: false,
    paymentMethodAvailable: false,
    isOpen: isSelected,
    acceptLoaded: false,
    clientToken: AcceptJsClientToken,
    isComplete: false,
    selectedPaymentProfileId: null,
    apiErrors: [],
    focused: undefined
  });

  const initAcceptJs = () => {
    if (typeof window === 'undefined') {
      console.log('prevented initAcceptJs');
      return;
    }

    if (state.acceptInitializing) {
      logDevError("Warning: AcceptJs already initialized or still initializing");
      return;
    }

    const script = document.createElement('script');
    script.src = settings.environment === 'production'
      ? AuthorizeNetScriptUrl.Production
      : AuthorizeNetScriptUrl.Sandbox;
    script.type = "text/javascript";
    document.head.appendChild(script);

    setState(prevState => ({ ...prevState, acceptInitializing: true, acceptLoaded: false }));

    GetPaymentMethods()
      .then((response) => {
        if (response.ok) return response.json();
        throw new Error(`${response.status}: ${response.statusText}`);
      })
      .then((paymentMethodsResult) => {
        if (paymentMethodsResult.success)
          onPaymentMethodsReceived(paymentMethodsResult.paymentMethods, props);
        else
          onAcceptDataLoadFailed(paymentMethodsResult.canRetry, paymentMethodsResult.errorMessage);
      }).catch((error) => {
        onAcceptDataLoadFailed(false, error.message);
      });
        }

  const submitHandler = () => {
    const authData = {
      apiLoginID: settings.apiLoginId,
      clientKey: settings.clientKey
    };
    const { cardCode, cardNumber, expDate, firstName, lastName } = values;
    const [month, year] = expDate.split('/');
    const fullName = firstName && lastName && (firstName + ' ' + lastName);
    const cardData = {
      cardCode,
      cardNumber: cardNumber.replace(/\s/g, ''),
      month,
      year,
      fullName
    };
    const secureData = { authData, cardData };

    return dispatchData(secureData)
      .then(response => {
        // Process API response on backend...
        const { firstName, lastName } = values;
        onSuccesfulRequest({ ...response, firstName, lastName }, acceptJsPaymentId);
        return response;
      })
      .catch(response => {
        setState(prevState => ({
          ...prevState,
          apiErrors: response.messages.message.map((err) => err.text),
          status: ["failure", response.messages.message.map(err => err.text)]
        }));
        throw response;
      });
  };

  const dispatchData = (payload) => {
    return new Promise((resolve, reject) =>
      window.Accept.dispatchData(payload, response => {
        switch (response.messages.resultCode) {
          case 'Ok':
            resolve(response);
            break;
          case 'Error':
            reject(response);
            break;
        }
      })
    );
  };

  const blurHandler = (field, ev) => {
    if (ev.target.value !== state[field]){
      onAcceptCardDataUpdate({
        ...values,
        [field]: ev.target.value
      });
    }
    setState(prevState => ({ ...prevState, focused: undefined }));
    return ev;
  };

  const methodSelectHandler = (paymentProfileId, ev) => {
    setState(prevState => ({ ...prevState, selectedPaymentProfileId: paymentProfileId }));

    if (paymentProfileId !== null && paymentProfileId !== 'add') {
      onProfilePaymentSelected(paymentProfileId, acceptJsPaymentId);
    } else {
      onProfilePaymentCleared();
    }

    return ev;
  };

  const methodDeleteHandler = (paymentProfileId, ev) => {
    DeletePaymentMethod(paymentProfileId)
      .then((response) => {
        if (response.ok) return response.json();
        throw new Error(`${response.status}: ${response.statusText}`);
      })
      .then((paymentMethodsResult) => {
        if (paymentMethodsResult.success) {
          onProfilePaymentCleared();
          onPaymentMethodsReceived(paymentMethodsResult.paymentMethods, props);
        } else {
          onAcceptDataLoadFailed(paymentMethodsResult.canRetry, paymentMethodsResult.errorMessage);
        }
      }).catch((error) => {
        onAcceptDataLoadFailed(false, error.message);
      });
  };

  const methodUpdateHandler = (paymentProfile, ev) => {
    UpdatePaymentMethod(paymentProfile)
      .then((response) => {
        if (response.ok) return response.json();
        throw new Error(`${response.status}: ${response.statusText}`);
      })
      .then((paymentMethodsResult) => {
        if (paymentMethodsResult.success) {
          onPaymentMethodsReceived(paymentMethodsResult.paymentMethods, props);
          methodSelectHandler(paymentProfile.customerPaymentProfileId, ev);
        } else {
          onAcceptDataLoadFailed(paymentMethodsResult.canRetry, paymentMethodsResult.errorMessage);
        }
      }).catch((error) => {
        onAcceptDataLoadFailed(false, error.message);
      });
  };

  useEffect(() => {
    if (isSelected) {
      if (!state.isOpen) {
        setState(prevState => ({ ...prevState, isOpen: true }));
      }
    }
    if (!state.acceptLoaded && !state.acceptInitializing) {
      initAcceptJs();
    }
  }, [isSelected]);

  useEffect(() => {
    if (isSelected && (!state.clientToken || state.clientToken.length < 1)) {
      setState(prevState => ({ ...prevState, isOpen: true }));
      if (!state.acceptLoaded && !state.acceptInitializing) {
        initAcceptJs();
      }
    }
  }, []);

  useEffect(() => {
    return () => {
      const head = document.head;
      const scripts = head.getElementsByTagName('script');

      Array.from(scripts)
        .filter(
          script =>
            script.src === AuthorizeNetScriptUrl.Production ||
            script.src === AuthorizeNetScriptUrl.Sandbox
        )
        .forEach(injectedScript => head.removeChild(injectedScript));
    };
  }, []);

  const { isOpen, apiErrors, focused } = state;
  const paymentMethods = AcceptJsClientPaymentMethods;
  const selectedPaymentProfileId = AuthorizeDotNetPaymentProfileId || state.selectedPaymentProfileId;

  if (paymentMethods && paymentMethods.length > 0) {
    return (
      <div className="acceptJsPaymentList" hidden={!isOpen}>
        {paymentMethods.map((paymentMethod, i) => (
          <AuthorizeProfilePaymentComponent
            key={i}
            paymentMethod={paymentMethod}
            onSelect={methodSelectHandler}
            onUpdate={methodUpdateHandler}
            onDelete={methodDeleteHandler}
            isSelected={selectedPaymentProfileId === paymentMethod.customerPaymentProfileId}
            countriesStatesMap={countriesStatesMap}
          />
        ))}
        <div className="acceptJsPayment">
          <OptionListItemRadio
            id={'Add'}
            label={' Add New '}
            isSelected={selectedPaymentProfileId === 'add'}
            onClick={(ev) => methodSelectHandler('add', ev)}
            disabled={false}
          >
            <AcceptPaymentForm
              hidden={selectedPaymentProfileId !== 'add'}
              amount={amount}
              apiErrors={apiErrors}
              handleBlur={blurHandler}
              handleSubmit={submitHandler}
              values={values}
              buttonText={'Add Card'}
            />
          </OptionListItemRadio>
        </div>
      </div>
    );
  }

  return (
    <div className="acceptJsPayment" hidden={!isOpen}>
      <AcceptPaymentForm
        amount={amount}
        apiErrors={apiErrors}
        focused={focused}
        //handleChange={changeHandler}
        //handleFocus={focusHandler}
        handleBlur={blurHandler}
        handleSubmit={submitHandler}
        values={values}
        buttonText={'Add Card'}
      />
    </div>
  );
};

const mapAcceptJsPaymentStateToProps = (state, ownProps) => {
  const settings = state.constants.acceptJsSettings;
  const entities = state.entities;
  const addresses = entities.Addresses;
  const summary = entities.Summary;

  return {
    settings,
    amount: summary.total,
    billingAddress: addresses.filter(a => a.Id === entities.SelectedBillingAddressId)[0],
    acceptJsPaymentId: settings.acceptJsPaymentId,
    placeOrderDetails: mapStateToPlaceOrderDetails(state),
    values: state.entities.PaymentRecord.AcceptJsCardValues || {
      cardNumber: '',
      cardCode: '',
      expDate: '',
      firstName: '',
      lastName: ''
    },
    AcceptJsClientToken: state.entities.PaymentRecord.AcceptJsClientToken,
    AcceptJsClientPaymentMethods: state.entities.PaymentRecord.AcceptJsClientPaymentMethods,
    AuthorizeDotNetPaymentProfileId: state.entities.PaymentRecord.AuthorizeDotNetPaymentProfileId || (state.entities.PaymentRecord.AcceptJsPaymentOpaqueData ? 'add' : null),
    countriesStatesMap: state.constants.countriesStatesMap,
    ...ownProps
  };
};

const mapAcceptJsPaymentDispatchToProps = (dispatch, ownProps) => {
  return {
    onClientTokenReceived: (payload, nextProps) => {
      dispatch(updateAcceptJsClientToken(payload));
    },
    onPaymentMethodsReceived: (payload, nextProps) => {
      dispatch(updateAcceptJsClientPaymentMethods(payload));
    },
    onPaymentMethodReceived: (payload, nextProps) => {
      dispatch(updateAcceptJsClientPaymentMethods(payload));
    },
    onAcceptDataLoadFailed: (canRetry, serverMessage) => {
      const retryMessage = (canRetry) ? 'Please reload the page and try again.' : 'Please contact customer service.';
      const text = `An error occurred with our servers. ${retryMessage}`;
      dispatch(notifyError(text, `message from server: "${serverMessage}"`));
    },
    logDevError: (text) => dispatch(logError(text)),
    onProfilePaymentCleared: () => {
      dispatch(clearAcceptJsClientPaymentProfile());
    },
    onProfilePaymentSelected: (customerPaymentProfileId, acceptJsPaymentId) => {
      ownProps.onPaymentIsValid();
      dispatch(selectPaymentOption(acceptJsPaymentId, "AcceptJs"));
      dispatch(selectAcceptJsClientPaymentProfile(customerPaymentProfileId));
    },
    onAcceptCardDataUpdate: (payload) => {
      dispatch(updateAcceptJsPaymentCard(payload));
    },
    onSuccesfulRequest: (payload, acceptJsPaymentId) => {
      ownProps.onPaymentIsValid();
      dispatch(selectPaymentOption(acceptJsPaymentId, "AcceptJs"));
      dispatch(updateAcceptJsPaymentData(payload));
    },
    onStartOver: () => ownProps.onPaymentCancel()
  };
};

const AcceptJsPaymentComponentWrapper = connect(mapAcceptJsPaymentStateToProps, mapAcceptJsPaymentDispatchToProps)(AcceptJsPaymentComponent);
export default AcceptJsPaymentComponentWrapper;